import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react'
import {ProductTileCms, ProductTileSkeleton} from '@/components/product-tile-cms'
import {useGetAccountFormData} from '@/builder/hooks/useGetAccountFormData'
import modelMap from '@/builder/model-map'
import PropTypes from 'prop-types'
import {isEmptyObject} from '@/utils/common'
import {PaginatedGridDisplay} from '@/components/paginated-grid-display'
import {usePlpPagination} from './usePlpPagination'
import {useInjectableGridContent} from '../../hooks/useInjectableGridContent'
import {useQueries} from '@tanstack/react-query'
import {fetchOneEntry} from '@builder.io/sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {injectedGridContentItemsDefinition} from './constants/injectedGridContent'
import {SeoCopyBlock} from '@/components/seo-copy-block/seoCopyBlock'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'
import {accountFormLabelInput} from '../account-drawer/constants/accountFormLabelInput'
import {useSitePrefs} from 'hec/hooks/useSitePrefs'

const gridItemClassnames = 'p-1 md:p-2 lg:p-4'

export const CategoryProductGrid = ({
    accountFormLabels,
    perPageLimit = 60,
    injectedContentItems = [],
    attributes
}) => {
    const apiKey = getConfig().app.builder.api
    const {
        showNextEntries,
        showAll,
        getAllEntries,
        totalItemsCount,
        fetchedProductData,
        allEntriesDisplayed,
        isLoading,
        isFetchingNextPage,
        error: error,
        selectedFacetFilters,
        selectedSortOption,
        productCategoryId
    } = usePlpPagination({perPageLimit})

    const isQueryEnabled = injectedContentItems.some((item) => !item?.content?.content?.value)

    // Trigger parallel fetches
    const fetchQueries = useQueries({
        queries: injectedContentItems.map((item) => {
            const id = item?.content?.content?.id
            const model = item?.content?.content?.model
            return {
                queryKey: [id, model],
                queryFn: () => fetchOneEntry({apiKey, model, query: {id}}),
                enabled: isQueryEnabled && !!id && !!model
            }
        })
    })

    const isLoadingQueries = fetchQueries.some((q) => q.isLoading)

    const {
        enableProductMessaging,
        lmnBadgeColor,
        lmnCallToAction,
        lmnModalContent,
        lmnModalLink,
        lmnModalTitle
    } = useSitePrefs()

    const injectedContentItemsData = useMemo(() => {
        if (isQueryEnabled && !isLoadingQueries) {
            return injectedContentItems.map((item) => {
                const content = item?.content?.content
                const matchingQuery = fetchQueries.find(({data}) => data?.id === content?.id)

                const newItem = {...item}

                if (matchingQuery?.data && content) {
                    newItem.content = {
                        ...item.content,
                        content: {
                            ...item.content.content,
                            value: matchingQuery.data
                        }
                    }
                }

                return newItem
            })
        }
        return injectedContentItems
    }, [injectedContentItems, isQueryEnabled, isLoadingQueries])

    const transformedInjectedContentItems = useInjectableGridContent(
        injectedContentItemsData,
        gridItemClassnames
    )

    const [products, setProducts] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const openModal = useCallback(() => setIsModalOpen(true), [])
    const closeModal = useCallback(() => setIsModalOpen(false), [])

    const selectedFacetsLength = Object.keys(selectedFacetFilters).length
    const prevSelectedFacetFilters = useRef(selectedFacetsLength)
    const noSelectedFacetFilters = selectedFacetsLength === 0
    const noPrevSelectedFacetFilters = prevSelectedFacetFilters.current >= 1

    useEffect(() => {
        if (!fetchedProductData || isEmptyObject(fetchedProductData)) return

        setProducts((prevProducts) => {
            const prevProductIds = new Set(prevProducts.map((p) => p.id))

            const newProducts = fetchedProductData.filter(
                (product) => !prevProductIds.has(product.id)
            )

            if (noSelectedFacetFilters && noPrevSelectedFacetFilters) {
                return [...prevProducts, ...newProducts]
            }

            return [...fetchedProductData]
        })
    }, [
        fetchedProductData,
        isFetchingNextPage,
        selectedSortOption,
        selectedFacetFilters,
        noSelectedFacetFilters,
        noPrevSelectedFacetFilters
    ])

    const productTiles = useMemo(() => {
        return products?.map((product, idx) => {
            return (
                <ProductTileCms
                    product={product}
                    isAddToCart={false}
                    enableQuickView={true}
                    enableProductMessaging={enableProductMessaging}
                    key={`product-tile-${product.id}-${idx}`}
                    className={gridItemClassnames}
                    data-testid="categoryProductGrid-ProductTileCms"
                    onOpenModal={openModal}
                    lmnCallToAction={lmnCallToAction}
                    lmnBadgeColor={lmnBadgeColor}
                />
            )
        })
    }, [products, openModal])

    if (isLoading && !products.length) {
        const loadingTiles = Array.from({length: perPageLimit}, (_, idx) => (
            <div className={gridItemClassnames} key={`product-tile-skeleton-${idx}`}>
                <ProductTileSkeleton data-testid="categoryProductGrid-ProductTileSkeleton" />
            </div>
        ))
        return (
            <PaginatedGridDisplay
                attributes={attributes}
                displayedItems={loadingTiles}
                injectedContentItems={transformedInjectedContentItems}
                data-testid="categoryProductGrid-PaginatedGridDisplay"
            />
        )
    }

    if (error) {
        console.error('Error fetching products:', error)
        return <h1 className="text-9xl">Failed to load products 💀</h1>
    }

    const displayedItemsText = `Showing ${productTiles.length} of ${totalItemsCount} products`

    return (
        <>
            <Dialog
                className="gap-5"
                open={isModalOpen}
                onOpenChange={(open) => (open ? openModal() : closeModal())}
            >
                <DialogContent data-testid="categoryProductGrid-Dialog">
                    <DialogHeader>
                        <DialogTitle data-testid="categoryProductGrid-DialogTitle">
                            {lmnModalTitle}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogDescription
                        className="text-[16px] text-[#49454f] font-grotesque"
                        data-testid="categoryProductGrid-DialogDescription"
                    >
                        {lmnModalContent}
                    </DialogDescription>
                    {lmnModalLink && (
                        <a
                            href={lmnModalLink}
                            className="text-accent underline font-medium"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Learn More
                        </a>
                    )}
                </DialogContent>
            </Dialog>
            <PaginatedGridDisplay
                attributes={attributes}
                displayedItems={productTiles}
                displayedItemsText={displayedItemsText}
                showNextEntries={showNextEntries}
                showAllEntries={getAllEntries}
                showAll={showAll}
                allEntriesDisplayed={allEntriesDisplayed}
                injectedContentItems={transformedInjectedContentItems}
                data-testid="categoryProductGrid-PaginatedGridDisplay"
            />
            {productCategoryId && (
                <SeoCopyBlock
                    category={productCategoryId}
                    data-testid="categoryProductGrid-SeoCopyBlock"
                />
            )}
        </>
    )
}

CategoryProductGrid.propTypes = {
    accountFormLabels: PropTypes.object.isRequired,
    perPageLimit: PropTypes.number,
    injectedContentItems: PropTypes.arrayOf(
        PropTypes.shape({
            content: PropTypes.object.isRequired,
            position: PropTypes.number.isRequired,
            desktopSpan: PropTypes.string.isRequired,
            mobileSpan: PropTypes.string.isRequired
        })
    ),
    attributes: PropTypes.object
}

export const categoryProductGridPerPageLimitInputField = {
    name: 'perPageLimit',
    type: 'number',
    defaultValue: 60,
    required: true,
    helperText: 'The number of products to display per page'
}

export const CategoryProductGridDefinition = {
    component: CategoryProductGrid,
    name: 'CategoryProductGrid',
    noWrap: true,
    inputs: [
        categoryProductGridPerPageLimitInputField,
        accountFormLabelInput,
        injectedGridContentItemsDefinition
    ]
}
